import {
  getInitialWindowSpec,
  getPresetIds,
} from '@cutr/constants/windows/presets';
import { WindowPresetId } from '@cutr/constants/windows/presets';
import { FrameShape } from '@cutr/constants/windows/windows-geometry';
import { getAreas } from '@cutr/constants/windows/windows-geometry.ts';
import { useFlag } from '@unleash/proxy-client-react';
import { t } from 'i18next';
import { createContext, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/primitives/Checkbox';

import { usePositionStore } from '../api/positionStore';
import { PresetThumbnailCard } from '../blocks/PresetThumbnailCard';
import { useUpdatePosition } from '../queries/position/crud';
import styles from './PresetSelect.module.css';

type FrameShapeType = FrameShape['frameType'];

interface PresetFilters {
  sashCount: number[];
  mullionConfiguration: ('with' | 'without')[];
  shape: FrameShapeType[];
}

interface PresetFilterContextType {
  filters: PresetFilters;
  setSashCount: (sashCount: number[]) => void;
  setMullionConfiguration: (
    mullionConfiguration: ('with' | 'without')[]
  ) => void;
  setShape: (shape: FrameShapeType[]) => void;
  clearFilters: () => void;
}

// Create the context
const PresetFilterContext = createContext<PresetFilterContextType | undefined>(
  undefined
);

// Custom hook to use the filter context
const usePresetFilters = () => {
  const context = useContext(PresetFilterContext);
  if (!context) {
    throw new Error(
      'usePresetFilters must be used within a PresetFilterProvider'
    );
  }
  return context;
};

// Filter provider component
const PresetFilterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [filters, setFilters] = useState<PresetFilters>({
    sashCount: [],
    mullionConfiguration: [],
    shape: [],
  });

  const setSashCount = (sashCount: number[]) => {
    setFilters((prev) => ({ ...prev, sashCount }));
  };

  const setMullionConfiguration = (
    mullionConfiguration: ('with' | 'without')[]
  ) => {
    setFilters((prev) => ({ ...prev, mullionConfiguration }));
  };

  const setShape = (shape: FrameShapeType[]) => {
    setFilters((prev) => ({ ...prev, shape }));
  };

  const clearFilters = () => {
    setFilters({
      sashCount: [],
      mullionConfiguration: [],
      shape: [],
    });
  };

  const value = {
    filters,
    setSashCount,
    setMullionConfiguration,
    setShape,
    clearFilters,
  };

  return (
    <PresetFilterContext.Provider value={value}>
      {children}
    </PresetFilterContext.Provider>
  );
};

export const PresetSelect = () => {
  return (
    <PresetFilterProvider>
      <div className={styles.layout}>
        <Filters />
        <Presets />
      </div>
    </PresetFilterProvider>
  );
};

const Presets = () => {
  const presetIds = getPresetIds();
  const { isPending } = useUpdatePosition();
  const { setPresetId, setWindowSpec } = usePositionStore();
  const { filters } = usePresetFilters();
  const isArcPresetsEnabled = useFlag('windows-arc-presets');

  const presetClickListener = async (presetId: WindowPresetId) => {
    const windowSpec = getInitialWindowSpec(presetId);
    setWindowSpec(windowSpec);
    setPresetId(presetId);
  };

  const visiblePresetIds = useMemo(() => {
    const filtered = presetIds.filter((presetId) => {
      const windowSpec = getInitialWindowSpec(presetId);
      const areas = getAreas(windowSpec);

      if (
        !isArcPresetsEnabled &&
        windowSpec.frameShape.frameType === 'rectangle-arc-top'
      ) {
        return false;
      }

      // Filter by sash count
      if (filters.sashCount.length > 0) {
        if (!filters.sashCount.includes(areas.length)) {
          return false;
        }
      }

      // Filter by shape
      if (filters.shape.length > 0) {
        if (!filters.shape.includes(windowSpec.frameShape.frameType)) {
          return false;
        }
      }

      return true;
    });

    const indexById = new Map(presetIds.map((id, i) => [id, i]));
    const sashCountById = new Map(
      presetIds.map((id) => [id, getAreas(getInitialWindowSpec(id)).length])
    );

    return [...filtered].sort((a, b) => {
      const sa = sashCountById.get(a) ?? 0;
      const sb = sashCountById.get(b) ?? 0;
      if (sa !== sb) return sa - sb;
      return (indexById.get(a) ?? 0) - (indexById.get(b) ?? 0);
    });
  }, [presetIds, filters, isArcPresetsEnabled]);

  return (
    <div className={styles.presetsLayout}>
      <h3>{t('reseller.presetSelection.choosePreset')}</h3>
      <div className={styles.presets}>
        {visiblePresetIds.map((presetId) => (
          <button
            key={presetId}
            name="presetId"
            onClick={() => presetClickListener(presetId)}
            disabled={isPending}
          >
            <PresetThumbnailCard presetId={presetId} />
          </button>
        ))}
      </div>
    </div>
  );
};

const Filters = () => {
  const { t } = useTranslation();
  const isArcPresetsEnabled = useFlag('windows-arc-presets');

  return (
    <div className={styles.filters}>
      <h3>{t('reseller.presetSelection.filterBy')}</h3>
      <SashCountFilter />
      {/* <MullionConfigurationFilter /> */}
      {isArcPresetsEnabled && <ShapeFilter />}
    </div>
  );
};

const SashCountFilter = () => {
  const { t } = useTranslation();
  const { filters, setSashCount } = usePresetFilters();

  const handleSashCountChange = (count: number, checked: boolean) => {
    setSashCount(
      checked
        ? [...filters.sashCount, count]
        : filters.sashCount.filter((v) => v !== count)
    );
  };

  return (
    <div className={styles.group}>
      <strong>{t('reseller.presetSelection.sashCount.label')}</strong>
      <Checkbox
        checked={filters.sashCount.includes(1)}
        onChange={(e) => handleSashCountChange(1, e.currentTarget.checked)}
      >
        {t('reseller.presetSelection.sashCount.options.one')}
      </Checkbox>
      <Checkbox
        checked={filters.sashCount.includes(2)}
        onChange={(e) => handleSashCountChange(2, e.currentTarget.checked)}
      >
        {t('reseller.presetSelection.sashCount.options.two')}
      </Checkbox>
      <Checkbox
        checked={filters.sashCount.includes(3)}
        onChange={(e) => handleSashCountChange(3, e.currentTarget.checked)}
      >
        {t('reseller.presetSelection.sashCount.options.three')}
      </Checkbox>
      <Checkbox
        checked={filters.sashCount.includes(4)}
        onChange={(e) => handleSashCountChange(4, e.currentTarget.checked)}
      >
        {t('reseller.presetSelection.sashCount.options.four')}
      </Checkbox>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MullionConfigurationFilter = () => {
  const { t } = useTranslation();
  const { filters, setMullionConfiguration } = usePresetFilters();

  const handleMullionChange = (
    config: 'with' | 'without',
    checked: boolean
  ) => {
    setMullionConfiguration(
      checked
        ? [...filters.mullionConfiguration, config]
        : filters.mullionConfiguration.filter((v) => v !== config)
    );
  };

  return (
    <div className={styles.group}>
      <strong>
        {t('reseller.presetSelection.mullionConfiguration.label')}
      </strong>
      <Checkbox
        checked={filters.mullionConfiguration.includes('with')}
        onChange={(e) => handleMullionChange('with', e.currentTarget.checked)}
      >
        {t('reseller.presetSelection.mullionConfiguration.options.withMullion')}
      </Checkbox>
      <Checkbox
        checked={filters.mullionConfiguration.includes('without')}
        onChange={(e) =>
          handleMullionChange('without', e.currentTarget.checked)
        }
      >
        {t(
          'reseller.presetSelection.mullionConfiguration.options.withoutMullion'
        )}
      </Checkbox>
    </div>
  );
};

const ShapeFilter = () => {
  const { t } = useTranslation();
  const { filters, setShape } = usePresetFilters();
  const isArcPresetsEnabled = useFlag('windows-arc-presets');

  const handleShapeChange = (shape: FrameShapeType, checked: boolean) => {
    setShape(
      checked
        ? [...filters.shape, shape]
        : filters.shape.filter((v) => v !== shape)
    );
  };

  return (
    <div className={styles.group}>
      <strong>{t('reseller.presetSelection.shape.label')}</strong>
      <Checkbox
        checked={filters.shape.includes('rectangle')}
        onChange={(e) =>
          handleShapeChange('rectangle', e.currentTarget.checked)
        }
      >
        {t('reseller.presetSelection.shape.options.rectangle')}
      </Checkbox>
      {isArcPresetsEnabled && (
        <Checkbox
          checked={filters.shape.includes('rectangle-arc-top')}
          onChange={(e) =>
            handleShapeChange('rectangle-arc-top', e.currentTarget.checked)
          }
        >
          {t('reseller.presetSelection.shape.options.rectangle-arc-top')}
        </Checkbox>
      )}
    </div>
  );
};
